import { useAppInitTask, useIfUser } from '@if/mui'
import { useMsal } from '@azure/msal-react'
import { isFederatedUser } from '@src/authConfig'
import { useGetUserProfile } from '@src/data/api/user-api/user-api'
import { LanguageCode, parseLanguageCode } from '@src/data/language-selectors'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

// Read before any render: i18next writes this key during init, so reading it later tells us nothing.
const hasBrowserLanguage = localStorage.getItem('i18nextLng') !== null

/** The account's language, else English — the claim answers on the first render, the query refreshes it after a save. */
export const useProfileLanguage = (): LanguageCode => {
  const user = useIfUser()
  const { accounts } = useMsal()
  const [profile] = useGetUserProfile(user?.id, isFederatedUser(accounts[0]))

  return parseLanguageCode(profile?.language ?? user?.language) ?? 'en'
}

/** Applies the dashboard's display language at boot — a language this browser remembers wins, else the account's. */
export const useApplyDisplayLanguage = (): void => {
  const { i18n } = useTranslation()
  const profileLanguage = useProfileLanguage()
  const [applied, setApplied] = useState(hasBrowserLanguage)

  useAppInitTask(applied, 'language')

  useEffect(() => {
    if (applied) return
    void i18n.changeLanguage(profileLanguage).finally(() => setApplied(true))
  }, [applied, profileLanguage, i18n])
}
